import { useCallback, useRef } from 'react';

import PropTypes from 'prop-types';
import { BsLock, BsPencil, BsTrash } from 'react-icons/bs';

import IconButton from '#globalComponents/IconButton/IconButton';
import Tag from '#globalComponents/Tag/Tag';
import Tooltip from '#globalComponents/Tooltip/Tooltip';
import { formatCount } from '#lib/numberFormat';

import { CREDIT_TYPES } from '../creditTypes';

import { LimitPopover } from './LimitPopover';
import RemoveConfirmation from './RemoveConfirmation';

export const MEMBER_GRID = 'grid grid-cols-[minmax(200px,1.4fr)_minmax(140px,1fr)_minmax(140px,1fr)_128px] gap-x-6';

const ROLE_LABELS = { owner: 'Eigentümer', admin: 'Admin', member: 'Mitglied' };
const NEAR_LIMIT_PERCENT = 90;

const isLimitReached = (member) =>
	CREDIT_TYPES.some(({ key }) => member[`${key}Limit`] !== null && member[`${key}Used`] >= member[`${key}Limit`]);

const UsageCell = ({ type, member, quota }) => {
	const limit = member[`${type.key}Limit`];
	const used = Math.max(0, member[`${type.key}Used`]);
	const cap = limit ?? quota;
	const percent = cap > 0 ? Math.min(100, (used / cap) * 100) : 100;
	let barClass = 'bg-primary-500';
	if (limit === null) barClass = 'bg-bgBase-600';
	else if (percent >= NEAR_LIMIT_PERCENT) barClass = 'bg-caution-500';

	return (
		<div className="min-w-[140px]">
			<div className="flex items-baseline justify-between text-sm">
				<span className="font-medium tabular-nums text-text">{formatCount(used)}</span>
				<span className="whitespace-nowrap text-xs tabular-nums text-bgBase-800">
					{limit === null ? 'kein Limit' : `von ${formatCount(limit)}`}
				</span>
			</div>
			<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-bgBase-400">
				<div
					className={`h-full rounded-full ${barClass}`}
					style={{ width: `${limit === null ? Math.max(2, percent) : percent}%` }}
				/>
			</div>
		</div>
	);
};

UsageCell.propTypes = {
	type: PropTypes.object.isRequired,
	member: PropTypes.object.isRequired,
	quota: PropTypes.number.isRequired,
};

const RoleControl = ({ member, canChangeRole, onRoleChange, changeRolePending }) =>
	canChangeRole ? (
		<select
			aria-label="Rolle ändern"
			value={member.role}
			onChange={(e) => onRoleChange(member.userId, e.target.value)}
			disabled={changeRolePending}
			className="h-6 w-auto flex-shrink-0 rounded border border-bgBase-500 bg-white px-1.5 text-xs text-text focus:border-primary-300 focus:outline-none"
		>
			<option value="admin">Admin</option>
			<option value="member">Mitglied</option>
		</select>
	) : (
		<Tag
			label={ROLE_LABELS[member.role] || member.role}
			tone={member.role === 'member' ? 'neutral' : 'primary'}
			size="xs"
		/>
	);

RoleControl.propTypes = {
	member: PropTypes.object.isRequired,
	canChangeRole: PropTypes.bool.isRequired,
	onRoleChange: PropTypes.func.isRequired,
	changeRolePending: PropTypes.bool.isRequired,
};

const MemberIdentity = ({ member, isCurrentUser, roleControl }) => {
	const displayName = member.fullName || member.email;
	return (
		<div className="flex min-w-0 items-center gap-3">
			<div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-500">
				<span className="text-xs font-semibold text-white">{(displayName || '?')[0].toUpperCase()}</span>
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex min-w-0 items-center gap-2">
					<span className="truncate text-sm font-medium text-text">
						{displayName}
						{isCurrentUser && <span className="font-normal text-bgBase-700"> (Du)</span>}
					</span>
					{roleControl}
				</div>
				<div className="flex min-w-0 items-center gap-2 text-xs text-bgBase-800">
					{member.fullName && <span className="truncate">{member.email}</span>}
					{isLimitReached(member) && (
						<Tooltip
							content={
								<span className="block w-56 text-xs">
									Limit erreicht – Suchen sind bis zum Monatswechsel gesperrt.
								</span>
							}
						>
							<span className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-caution-200 px-1.5 py-0.5 text-[10.5px] font-medium text-caution-700">
								<BsLock className="h-3 w-3" />
								gesperrt
							</span>
						</Tooltip>
					)}
				</div>
			</div>
		</div>
	);
};

MemberIdentity.propTypes = {
	member: PropTypes.object.isRequired,
	isCurrentUser: PropTypes.bool.isRequired,
	roleControl: PropTypes.node,
};

const MemberRow = ({
	member,
	isCurrentUser,
	isOwner,
	quotas,
	renewal,
	isEditingLimit,
	isConfirmingRemove,
	transferDataOnRemove,
	onToggleLimit,
	onToggleRemove,
	onRemove,
	onRoleChange,
	onTransferDataToggle,
	changeRolePending,
	removeMutation,
}) => {
	const limitButtonRef = useRef(null);
	const closeLimit = useCallback(() => onToggleLimit(null), [onToggleLimit]);
	// Admins manage everyone but the owner; the owner may cap their own use too.
	const canEditLimit = isCurrentUser || member.role !== 'owner';
	const canRemove = !isCurrentUser && member.role !== 'owner';
	const canChangeRole = isOwner && !isCurrentUser && member.role !== 'owner';

	return (
		<div className="border-b border-bgBase-500 px-5 py-3.5 last:border-0">
			<div className={`${MEMBER_GRID} items-center`}>
				<MemberIdentity
					member={member}
					isCurrentUser={isCurrentUser}
					roleControl={
						<RoleControl
							member={member}
							canChangeRole={canChangeRole}
							onRoleChange={onRoleChange}
							changeRolePending={changeRolePending}
						/>
					}
				/>
				{CREDIT_TYPES.map((type) => (
					<UsageCell key={type.key} type={type} member={member} quota={quotas[type.key]} />
				))}
				<div className="relative flex items-center justify-end gap-1">
					{canEditLimit && (
						<button
							ref={limitButtonRef}
							type="button"
							onClick={() => onToggleLimit(member.userId)}
							className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-card border border-bgBase-500 px-2.5 text-xs font-medium text-text hover:bg-bgBase-300"
						>
							<BsPencil className="h-3.5 w-3.5" />
							Limit
						</button>
					)}
					{canRemove && (
						<IconButton
							icon={BsTrash}
							label="Mitglied entfernen"
							tone="danger"
							onClick={() => onToggleRemove(member.userId)}
						/>
					)}
					{isEditingLimit && (
						<LimitPopover
							member={member}
							quotas={quotas}
							renewal={renewal}
							anchorRef={limitButtonRef}
							onClose={closeLimit}
						/>
					)}
				</div>
			</div>

			{isConfirmingRemove && (
				<RemoveConfirmation
					member={member}
					transferDataOnRemove={transferDataOnRemove}
					onTransferDataToggle={onTransferDataToggle}
					onToggleRemove={onToggleRemove}
					onRemove={onRemove}
					removeMutation={removeMutation}
				/>
			)}
		</div>
	);
};

MemberRow.propTypes = {
	member: PropTypes.object.isRequired,
	isCurrentUser: PropTypes.bool.isRequired,
	isOwner: PropTypes.bool.isRequired,
	quotas: PropTypes.shape({ company: PropTypes.number.isRequired, contact: PropTypes.number.isRequired }).isRequired,
	renewal: PropTypes.string,
	isEditingLimit: PropTypes.bool.isRequired,
	isConfirmingRemove: PropTypes.bool.isRequired,
	transferDataOnRemove: PropTypes.bool.isRequired,
	onToggleLimit: PropTypes.func.isRequired,
	onToggleRemove: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	onRoleChange: PropTypes.func.isRequired,
	onTransferDataToggle: PropTypes.func.isRequired,
	changeRolePending: PropTypes.bool.isRequired,
	removeMutation: PropTypes.object.isRequired,
};

export default MemberRow;
